import { WALTER_CHAT_MESSAGE_KEY } from '../constants'
import { ChatFlowHandler } from '../types'
import { callWalterAPI } from './api'

export interface WalterState {
  userPrompt: string
  currentQuestion: string | null
  conversationId: string | null
  contextHistory: Array<{ role: string; content: string }>
  isGenerating: boolean
}


// Helper function to save messages to localStorage
const saveMessages = (
  contextHistory: Array<{ role: string; content: string }>
) => {
  try {
    const messages = contextHistory.map(msg => ({
      text: msg.content,
      sender: msg.role === 'user' ? 'user' : 'bot' // Convert 'assistant' to 'bot' for UI consistency
    }))
    localStorage.setItem(WALTER_CHAT_MESSAGE_KEY, JSON.stringify(messages))
  } catch (error) {
    console.error('Error saving messages to localStorage:', error)
  }
}

export const walterHandler: ChatFlowHandler = {
  getInitialMessages: () => {
    try {
      const savedMessages = localStorage.getItem(WALTER_CHAT_MESSAGE_KEY)
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages)
        // Ensure we have valid messages
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
        }
      }
    } catch (error) {
      console.error('Error loading saved messages:', error)
    }

    // Create and save default message if no saved messages or error
    const defaultMessages = [
      {
        text: "Hello! I'm Walter AI. How can I help you today?",
        sender: 'bot'
      }
    ]

    // Save the default message to localStorage
    try {
      localStorage.setItem(WALTER_CHAT_MESSAGE_KEY, JSON.stringify(defaultMessages))
    } catch (error) {
      console.error('Error saving default message:', error)
    }

    return defaultMessages
  },

  getInitialState: () => {
    // Load context history from localStorage if available
    try {
      const savedMessages = localStorage.getItem(WALTER_CHAT_MESSAGE_KEY)
      if (savedMessages) {
        const messages = JSON.parse(savedMessages)
        const contextHistory = messages.map(
          (msg: { sender: string; text: string }) => ({
            role: msg.sender === 'bot' ? 'assistant' : msg.sender,
            content: msg.text
          })
        )
        return {
          userPrompt: '',
          currentQuestion: '',
          conversationId: null,
          contextHistory,
          isGenerating: false
        } as WalterState
      }
    } catch (error) {
      console.error('Error loading context history:', error)
    }

    // If no saved messages, create initial context history with welcome message
    const initialContextHistory = [
      {
        role: 'assistant',
        content: "Hello! I'm Walter AI. How can I help you today?"
      }
    ]

    return {
      userPrompt: '',
      currentQuestion: '',
      conversationId: null,
      contextHistory: initialContextHistory,
      isGenerating: false
    } as WalterState
  },

  initialize: async (state: WalterState, sendMessage) => {
    if (state.currentQuestion) {
      await sendMessage(state.currentQuestion)
    }
  },

  handleUserInput: async (input: string, state: WalterState, sendMessage) => {
    const newState = { ...state }
    newState.isGenerating = true
    newState.userPrompt = input

    // Add user message to context history
    newState.contextHistory.push({
      role: 'user',
      content: input
    })

    try {
      // Make API call to Walter AI
      const response = await callWalterAPI({
        query: input
      })

      newState.isGenerating = false

      // Determine the actual message that will be sent to the user
      let assistantMessage = ''

      if (response.type === 'QnA' && response.status === 200) {
        assistantMessage =
          response?.result || response?.answer || 'No response available'
        await sendMessage(assistantMessage)
      } else if (response.type === 'navigation' && response.status === 200) {
        assistantMessage = response.message || 'Navigation successful'
        await sendMessage(assistantMessage)
      } else if (response.type === 'navigation' && response.status === 404) {
        assistantMessage = response.result || 'No route found'
        await sendMessage(assistantMessage)
      } else {
        // Fallback for other response types
        assistantMessage =
          response?.answer ||
          response?.message ||
          "I received your message but couldn't process it properly."
        await sendMessage(assistantMessage)
      }

      // Add the ACTUAL assistant message to context history
      newState.contextHistory.push({
        role: 'assistant',
        content: assistantMessage
      })

      // Save messages to localStorage
      saveMessages(newState.contextHistory)

      // Handle navigation if needed
      if (response.type === 'navigation' && response.status === 200) {
        return { newState, navigateTo: response.result }
      }

      return { newState }
    } catch (error) {
      newState.isGenerating = false
      console.error('Error with Walter AI:', error)

      const errorMessage =
        'I apologize, but I encountered an error processing your request. Please try again.'
      await sendMessage(errorMessage)

      // Add error message to context history
      newState.contextHistory.push({
        role: 'assistant',
        content: errorMessage
      })

      // Save messages to localStorage
      saveMessages(newState.contextHistory)

      return { newState }
    }
  }
}